export type ImportField =
  | "organizationName"
  | "category"
  | "website"
  | "organizationPhone"
  | "organizationAddress"
  | "contactName"
  | "contactTitle"
  | "workPhone"
  | "mobilePhone"
  | "fax"
  | "email"
  | "contactAddress";

export type ImportColumnMapping = Partial<Record<ImportField, string>>;

export type ImportRow = Record<ImportField, string | null> & { rowNumber: number };

export const importFieldDefinitions: Array<{ key: ImportField; label: string; required?: boolean; aliases: string[] }> = [
  { key: "organizationName", label: "Organization name", required: true, aliases: ["organization", "organization name", "company", "company name", "referral source", "source", "facility"] },
  { key: "category", label: "Source category", aliases: ["category", "source category", "referral category", "type"] },
  { key: "website", label: "Organization website", aliases: ["website", "web", "url", "company website"] },
  { key: "organizationPhone", label: "Organization phone", aliases: ["organization phone", "company phone", "main phone", "office phone"] },
  { key: "organizationAddress", label: "Organization address", aliases: ["organization address", "company address", "office address"] },
  { key: "contactName", label: "Contact name", aliases: ["contact", "contact name", "person", "name", "full name"] },
  { key: "contactTitle", label: "Contact title", aliases: ["contact title", "job title", "title", "role"] },
  { key: "workPhone", label: "Contact work phone", aliases: ["work phone", "contact phone", "phone", "direct", "direct phone", "p"] },
  { key: "mobilePhone", label: "Contact mobile", aliases: ["mobile", "mobile phone", "cell", "cell phone", "contact mobile", "c"] },
  { key: "fax", label: "Contact fax", aliases: ["fax", "fax phone", "f"] },
  { key: "email", label: "Contact email", aliases: ["email", "email address", "contact email", "e-mail"] },
  { key: "contactAddress", label: "Contact address", aliases: ["contact address", "mailing address"] },
];

function normalizedHeader(value: string): string {
  return value.trim().toLowerCase().replace(/[_-]+/g, " ").replace(/\s+/g, " ");
}

export function autoMapImportColumns(headers: string[]): ImportColumnMapping {
  const mapping: ImportColumnMapping = {};
  const used = new Set<string>();
  for (const field of importFieldDefinitions) {
    const match = headers.find((header) => !used.has(header) && field.aliases.some((alias) => normalizedHeader(alias) === normalizedHeader(header)));
    if (match) {
      mapping[field.key] = match;
      used.add(match);
    }
  }
  return mapping;
}

export function prepareImportHeaders(row: string[]): string[] {
  const counts = new Map<string, number>();
  return row.map((rawHeader, index) => {
    const base = rawHeader.replace(/^\uFEFF/, "").trim() || `Column ${index + 1}`;
    const count = (counts.get(base) ?? 0) + 1;
    counts.set(base, count);
    return count === 1 ? base : `${base} (${count})`;
  });
}

export function parseDelimitedText(text: string): string[][] {
  const sample = text.slice(0, 4000);
  const delimiter = (sample.match(/\t/g)?.length ?? 0) > (sample.match(/,/g)?.length ?? 0) ? "\t" : ",";
  const rows: string[][] = [];
  let row: string[] = [];
  let value = "";
  let quoted = false;
  for (let index = 0; index < text.length; index += 1) {
    const character = text[index];
    if (character === '"') {
      if (quoted && text[index + 1] === '"') {
        value += '"';
        index += 1;
      } else {
        quoted = !quoted;
      }
    } else if (character === delimiter && !quoted) {
      row.push(value.trim());
      value = "";
    } else if ((character === "\n" || character === "\r") && !quoted) {
      if (character === "\r" && text[index + 1] === "\n") index += 1;
      row.push(value.trim());
      if (row.some(Boolean)) rows.push(row);
      row = [];
      value = "";
    } else {
      value += character;
    }
  }
  row.push(value.trim());
  if (row.some(Boolean)) rows.push(row);
  return rows;
}

export async function readImportGrid(file: File): Promise<string[][]> {
  if (/\.xlsx$/i.test(file.name)) {
    const { readSheet } = await import("read-excel-file/browser");
    const rows = await readSheet(file);
    return rows.map((row) => row.map((cell) => cell === null || cell === undefined ? "" : String(cell).trim()));
  }
  return parseDelimitedText(await file.text());
}

export function mapImportRows(headers: string[], rows: string[][], mapping: ImportColumnMapping): ImportRow[] {
  const headerIndex = new Map(headers.map((header, index) => [header, index]));
  return rows
    .map((row, index) => ({ row, rowNumber: index + 2 }))
    .filter(({ row }) => row.some((cell) => cell.trim()))
    .map(({ row, rowNumber }) => {
      const mapped = Object.fromEntries(importFieldDefinitions.map((field) => {
        const header = mapping[field.key];
        const value = header === undefined ? "" : row[headerIndex.get(header) ?? -1] ?? "";
        return [field.key, value.trim() || null];
      })) as Record<ImportField, string | null>;
      return { rowNumber, ...mapped };
    });
}

export async function fileFingerprint(file: File): Promise<string> {
  const digest = await crypto.subtle.digest("SHA-256", await file.arrayBuffer());
  return Array.from(new Uint8Array(digest), (byte) => byte.toString(16).padStart(2, "0")).join("");
}
